import type { CardId } from "@/types/card"

/*
 * Card pitfalls — the "Be aware when…" warning each capability carries.
 *
 * Source is the v0 pitfalls set from Figma (🦾 AI Club, "Section 1"): one pitfall
 * per card, written and validated with the ML team but never printed on the first
 * physical deck. This file brings that content into the app, re-paired against the
 * CURRENT deck.
 *
 * Shape follows `patterns.ts` / `techStack.ts`: a map keyed by the 24 ACTIVE card
 * ids, resolved through `pitfallForCard`. Retired ids are absent on purpose — a
 * retired card is off the deck, so the browser never opens it and the resolver
 * answers null. The whole Monitors family (`f5*`) is therefore gone, and with it
 * its four pitfalls: they describe a family the re-cut retired, and some of their
 * theme already survives on `f3c3` (Predict Outcomes) and `f4c5` (Monitor &
 * Adjust Actions).
 *
 * The v0 grid could NOT be paired by position — its column order does not track
 * the card order (e.g. the Writes "mimics human emotion" pitfall belongs to Speak
 * Naturally, not to the first card). Pairing was rebuilt by meaning, anchored to
 * each card's `watchOut` line in `cards.ts`, then reviewed card by card.
 *
 * Each pitfall is ONE sentence — the v0 cause and consequence merged (Nov 2026,
 * reviewed and approved) and rendered as the single "Be aware when…" description.
 * See `docs/ai-pitfalls-merged-copy.md` for the before/after.
 *
 * `draft: true` marks a line WRITTEN OR REWORDED in that Nov 2026 review — it has
 * not been re-checked by the ML team, same footing as the notice atop
 * `deckExamples.ts`. An unflagged line is the original ML-validated v0 copy, merged
 * (`f3c4` keeps validated v0 text that was merely moved to a better-fitting card,
 * so it is not flagged). Clear the flags once someone who owns the content has
 * read them.
 */

export interface Pitfall {
  /** The warning as one line: the failure mode and what it costs, in a single
   *  sentence (the merged v0 cause + consequence). */
  text: string
  /** Written or reworded in the Nov 2026 pairing review; not yet re-validated. */
  draft?: boolean
}

/*
 * Keyed by the 24 active card ids, in each family's printed `cardIds` order (the
 * same order `patterns.ts` uses), so the two files read the same way.
 */
const CARD_PITFALLS: Record<CardId, Pitfall> = {
  // Understand the world
  f1c1: {
    text: "What AI sees reflects limited or skewed data sources, which can result in false positives and unreliable detection.",
  },
  f1c2: {
    text: "AI misreads slang, tone, or emotional cues, which can lead to inaccurate recognition or wrong conclusions.",
  },
  f1c5: {
    // Reworded to tie the risk to actually combining inputs, per review.
    text: "AI lets the clearest input override the others, so subtle but important signals get ignored.",
    draft: true,
  },
  f1c3: {
    text: "Training data reflects social or demographic bias, which can produce unfair or distorted interpretations.",
  },

  // Communicate & connect
  f2c1: {
    text: "AI generates plausible but false content, which can erode credibility and spread misinformation.",
  },
  f2c2: {
    text: "AI mimics human emotion without truly understanding it, so responses can feel hollow or manipulative.",
  },
  f2c3: {
    text: "AI assumes user intent based on limited data, which can create discomfort or a sense of being watched.",
  },
  f2c4: {
    text: "AI adapts tone too much or inconsistently, which can cause brand inconsistency or user confusion.",
  },

  // Think & reason
  f3c1: {
    // New copy — v0's "doesn't explain why" borrow did not fit retrieval.
    text: "AI retrieves from outdated or unreliable sources, spreading wrong answers faster than anyone can check them.",
    draft: true,
  },
  f3c2: {
    text: "AI claims to understand complex logic it can't fully model, creating overconfidence in wrong outputs.",
  },
  f3c3: {
    text: "AI presents uncertain results as exact numbers, which can mislead stakeholders and skew decision-making.",
  },
  f3c4: {
    // Validated v0 text, moved here from Partners where it fit Creative Ideas
    // better than the retired card it was written for — so not flagged draft.
    text: "AI generates ideas without understanding context or constraints, producing irrelevant or unfeasible concepts.",
  },

  // Take action
  f4c1: {
    text: "One wrong automated action triggers more failures, causing inefficiency or real-world damage.",
  },
  f4c5: {
    // Reworded out of engineer-speak ("no ceiling") into plain language.
    text: "AI keeps fixing its own mistakes with no one checking, so one small slip snowballs into a bigger mess.",
    draft: true,
  },
  f4c3: {
    // New copy — v0's "no handoff to a human" read as a service-design issue.
    text: "AI automates a broken process as-is, scaling the flaws instead of fixing them.",
    draft: true,
  },
  f4c4: {
    // Reworded to add the traceability angle, per review.
    text: "AI acts across systems with no clear owner and no trail of what it did, so errors can't be traced, fixed, or answered for.",
    draft: true,
  },

  // Explain & guide
  f6c1: {
    text: "AI explanations are too technical or incomplete, so users can't understand or trust the output.",
  },
  f6c2: {
    text: "AI gives suggestions without understanding user goals, which reduces agency and ownership.",
  },
  f6c3: {
    text: "AI expresses high certainty even when wrong, eroding trust once users notice inconsistencies.",
  },
  f6c4: {
    text: "AI overlooks fairness or safety implications, undermining user confidence.",
  },

  // Collaborate
  f7c1: {
    text: "AI dominates collaboration instead of augmenting it, reducing human creativity and engagement.",
  },
  f7c2: {
    text: "Humans either over- or under-trust AI suggestions, which leads to poor shared outcomes.",
  },
  f7c3: {
    // New copy — v0's "unclear boundaries" did not match the card.
    text: "AI hides the complexity behind a plain-language request, so users can't tell when and why it got the task wrong.",
    draft: true,
  },
  f7c4: {
    // Reworded to aim at whose assumptions drive the trade-off, avoiding the
    // overlap with `f1c3` (correlations as facts) and `f7c2` (mis-calibrated trust).
    text: "AI weighs trade-offs by its own assumptions and misses risks specific to your context, leaving hidden values and a false sense that ethics is covered.",
    draft: true,
  },
}

/**
 * The pitfall a card carries, or null for a card the set does not cover (a
 * retired id), so the caller can fall back rather than render an empty warning.
 */
export function pitfallForCard(id: CardId): Pitfall | null {
  return CARD_PITFALLS[id] ?? null
}
